import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Phone } from 'lucide-react';
import { companyInfo } from '../data/mock';

const ServiceModal = ({ service, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const nextImage = useCallback(() => {
    setCurrentImageIndex((prev) =>
      (prev + 1) % service.gallery.length
    );
  }, [service.gallery.length]);

  const prevImage = useCallback(() => {
    setCurrentImageIndex((prev) =>
      (prev - 1 + service.gallery.length) % service.gallery.length
    );
  }, [service.gallery.length]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextImage, onClose, prevImage]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/90 hover:bg-white text-[#1B3A3A] transition-colors"
          aria-label="Close modal"
        >
          <X size={24} />
        </button>

        <div className="grid md:grid-cols-2">
          {/* Image Gallery */}
          <div className="relative bg-gray-100">
            <div className="aspect-square md:h-full">
              <img
                src={service.gallery[currentImageIndex]}
                alt={`${service.title} - Image ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Gallery Navigation */}
            {service.gallery.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 hover:bg-white text-[#1B3A3A] transition-colors"
                  aria-label="Previous image"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 hover:bg-white text-[#1B3A3A] transition-colors"
                  aria-label="Next image"
                >
                  <ChevronRight size={20} />
                </button>

                {/* Image Dots */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {service.gallery.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${
                        index === currentImageIndex
                          ? 'bg-[#D3FF62] scale-125'
                          : 'bg-white/60 hover:bg-white/80'
                      }`}
                      aria-label={`View image ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Content */}
          <div className="p-8 flex flex-col">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1B3A3A] mb-4">
              {service.title}
            </h2>
            <p className="text-[#6B7B7B] mb-6 flex-grow">
              {service.description}
            </p>

            {/* CTA Buttons */}
            <div className="space-y-3">
              <a
                href="#contact"
                onClick={onClose}
                className="btn-primary w-full text-center justify-center"
              >
                Request a Quote
              </a>
              <a
                href={`tel:${companyInfo.phone}`}
                className="btn-secondary w-full text-center flex items-center justify-center gap-2"
              >
                <Phone size={18} />
                Call {companyInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceModal;
